import { useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@services/api';
import { toDisplayError } from '../../utils/apiError';

const schema = z.object({
  newEmail: z.string().trim().email('profile.email.errors.invalid'),
  currentPassword: z.string().min(1, 'profile.email.errors.passwordRequired'),
});
type FormData = z.infer<typeof schema>;

interface ChangeEmailDialogProps {
  open: boolean;
  currentEmail: string;
  onClose: () => void;
  /** Called with the address now waiting for confirmation. */
  onRequested: (pendingEmail: string) => void;
}

/**
 * REV-683: ask to change the account's email address. The API mails the new
 * address a confirmation link and the current one a notice; the address
 * changes only when the link is used. The current password is required.
 *
 * Validated on submit; the button is disabled only while the request is in
 * flight (never pre-disabled).
 */
export default function ChangeEmailDialog({
  open,
  currentEmail,
  onClose,
  onRequested,
}: ChangeEmailDialogProps) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { newEmail: '', currentPassword: '' },
  });

  const close = () => {
    form.reset();
    setError(null);
    onClose();
  };

  const onSubmit = async (data: FormData) => {
    // A repeat activation while one is in flight (the button is aria-disabled,
    // not disabled; see below).
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiClient.post<{ success: boolean; data: { pendingEmail: string } }>(
        '/v1/users/me/email-change',
        { newEmail: data.newEmail, currentPassword: data.currentPassword }
      );
      form.reset();
      onRequested(res.data.pendingEmail);
    } catch (err) {
      setError(toDisplayError(err, t('profile.email.errors.failed')));
    } finally {
      setSubmitting(false);
    }
  };

  const { errors } = form.formState;
  return (
    <Dialog
      open={open}
      onClose={close}
      aria-labelledby="change-email-title"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="change-email-title">{t('profile.email.dialogTitle')}</DialogTitle>
      <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('profile.email.dialogIntro', { email: currentEmail })}
          </DialogContentText>
          {error && (
            <Alert severity="error" role="alert" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            {...form.register('newEmail')}
            label={t('profile.email.newEmail')}
            type="email"
            autoComplete="email"
            fullWidth
            margin="dense"
            error={!!errors.newEmail}
            helperText={errors.newEmail ? t(errors.newEmail.message as string) : undefined}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            {...form.register('currentPassword')}
            label={t('profile.email.currentPassword')}
            type="password"
            autoComplete="current-password"
            fullWidth
            margin="dense"
            error={!!errors.currentPassword}
            helperText={
              errors.currentPassword ? t(errors.currentPassword.message as string) : undefined
            }
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </DialogContent>
        <DialogActions>
          {/* outlined: a text-variant button reads as plain text (CSS-16). */}
          <Button variant="outlined" onClick={close}>
            {t('profile.email.cancel')}
          </Button>
          {/* Never pre-disabled. While the request is in flight it is
              aria-disabled, not disabled: disabling the focused button moved
              focus off it, onto the dialog's container, so a wrong-password
              error left the keyboard user without their place; and swapping
              the label for a hidden spinner left the button with no name
              (REV-987, as on the team pages). */}
          <Button
            type="submit"
            variant="contained"
            aria-disabled={submitting || undefined}
            aria-busy={submitting || undefined}
            startIcon={
              submitting ? <CircularProgress size={16} color="inherit" aria-hidden /> : undefined
            }
          >
            {submitting ? t('profile.email.sending') : t('profile.email.send')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
